import React, { useState } from "react";
import {
  Scissors,
  Heart,
  Sparkles,
  Wrench,
  Shirt,
  ChevronDown,
  ChevronUp,
  Clock,
  Check,
} from "lucide-react";
import "./Services.css";

const Services = () => {
  const [expandedService, setExpandedService] = useState(null);

  const services = [
    {
      icon: Scissors,
      title: "Custom Tailoring",
      shortDescription:
        "Perfectly fitted garments crafted to your exact measurements and style preferences.",
      fullDescription:
        "Experience the luxury of made-to-measure clothing. Our custom tailoring service ensures every garment is crafted specifically for your body type, personal style, and lifestyle needs.",
      includes: [
        "Detailed body measurements and fitting sessions",
        "Premium fabric selection guidance",
        "Personalized design consultations",
        "Multiple fitting sessions for perfect fit",
        "Custom finishing and detailing options",
      ],
      turnaround: "2-3 weeks depending on complexity",
    },
    {
      icon: Heart,
      title: "Bridal Couture",
      shortDescription:
        "Exquisite bridal wear that makes your special day unforgettable with stunning designs.",
      fullDescription:
        "Your wedding day deserves nothing but perfection. Our bridal couture service specializes in creating breathtaking wedding ensembles that capture your vision and celebrate your unique love story.",
      includes: [
        "Comprehensive bridal consultation",
        "Custom lehenga, saree, and gown designs",
        "Premium embroidery and embellishments",
        "Coordinating outfits for wedding party",
        "Multiple fittings to ensure perfection",
        "Preservation and care instructions",
      ],
      turnaround: "4-8 weeks for complete bridal ensemble",
    },
    {
      icon: Sparkles,
      title: "Aari Work",
      shortDescription:
        "Intricate hand embroidery that adds traditional elegance to modern designs.",
      fullDescription:
        "Aari work is a centuries-old embroidery technique that adds exquisite detail and luxury to any garment. Our master artisans create stunning patterns using traditional methods passed down through generations.",
      includes: [
        "Traditional and contemporary Aari designs",
        "Custom pattern creation",
        "Premium thread and bead work",
        "Zari, sequin, and stone embellishments",
        "Suitable for sarees, blouses, lehengas, and gowns",
      ],
      turnaround: "3-6 weeks depending on design complexity",
    },
    {
      icon: Wrench,
      title: "Alterations & Repairs",
      shortDescription:
        "Expert alterations to ensure your garments fit perfectly and last longer.",
      fullDescription:
        "Extend the life of your favorite garments with our professional alteration and repair services. From simple hemming to complex restructuring, we handle all your tailoring needs.",
      includes: [
        "Hemming and length adjustments",
        "Taking in or letting out seams",
        "Zipper and button replacements",
        "Lining repairs and replacements",
        "Sleeve and shoulder adjustments",
        "Damage repair and restoration",
      ],
      turnaround: "3-7 days for most alterations",
    },
    {
      icon: Shirt,
      title: "All Type of Stitching Works",
      shortDescription:
        "Comprehensive stitching services for all your fashion needs and occasions.",
      fullDescription:
        "Whatever your stitching needs, we have the expertise to deliver. From everyday wear to special occasion outfits, our skilled tailors work with all types of fabrics and styles.",
      includes: [
        "Blouses and cholis",
        "Kurtis and salwar kameez",
        "Palazzo pants and trousers",
        "Traditional ethnic wear",
        "Contemporary fusion designs",
      ],
      turnaround: "1-3 weeks based on garment type",
    },
  ];

  const toggleService = (index) => {
    setExpandedService(expandedService === index ? null : index);
  };

  return (
    <div className="services-page">
      <section className="services-hero">
        <div className="services-hero-content">
          <h1 className="services-hero-title">Our Services</h1>
          <p className="services-hero-subtitle">
            Expert Craftsmanship for Every Occasion
          </p>
        </div>
      </section>

      <section className="services-intro">
        <div className="services-intro-container">
          <p className="services-intro-text">
            At PSD Designers, we offer a comprehensive range of tailoring and
            design services to meet all your fashion needs. Each service is
            delivered with meticulous attention to detail, premium quality
            materials, and a commitment to exceeding your expectations.
          </p>
        </div>
      </section>

      <section className="services-list">
        <div className="services-list-container">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            const isExpanded = expandedService === index;

            return (
              <div
                key={index}
                className={`service-detail-card ${
                  isExpanded ? "expanded" : ""
                }`}
              >
                <div
                  className="service-detail-header"
                  onClick={() => toggleService(index)}
                >
                  <div className="service-detail-title-group">
                    <div className="service-detail-icon">
                      <IconComponent size={28} />
                    </div>
                    <div>
                      <h2 className="service-detail-title">{service.title}</h2>
                      <p className="service-detail-short">
                        {service.shortDescription}
                      </p>
                    </div>
                  </div>
                  <button
                    className="service-toggle-btn"
                    aria-label="Toggle service details"
                  >
                    {isExpanded ? (
                      <ChevronUp size={24} />
                    ) : (
                      <ChevronDown size={24} />
                    )}
                  </button>
                </div>

                {isExpanded && (
                  <div className="service-detail-content">
                    <p className="service-detail-description">
                      {service.fullDescription}
                    </p>

                    <div className="service-detail-includes">
                      <h3 className="service-detail-includes-title">
                        What's Included:
                      </h3>
                      <ul className="service-detail-includes-list">
                        {service.includes.map((item, i) => (
                          <li key={i}>
                            <Check size={18} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="service-detail-turnaround">
                      <Clock size={20} />
                      <span>
                        <strong>Estimated Turnaround:</strong>{" "}
                        {service.turnaround}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section className="services-cta">
        <div className="services-cta-container">
          <h2 className="services-cta-title">
            Ready to Begin Your Custom Creation?
          </h2>
          <p className="services-cta-text">
            Contact us today to discuss your requirements and book a
            consultation
          </p>
          <a href="tel:+919585693060" className="services-cta-button">
            Call +91 95856 93060
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;
